import { useEffect, useState } from "react";
import axios from "axios";
import sharedStyles from "../styles/shared.module.css";
import adminStyles from "../styles/adminAvaliacao.module.css";
import { Check, X } from "lucide-react";

type Tab = "pendentes" | "todas";

const AdminAvaliacoes = () => {
  const [avaliacoes, setAvaliacoes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("pendentes");
  const [openDropdowns, setOpenDropdowns] = useState<number[]>([]); // IDs das avaliações abertas
  const BASE_URL = process.env.REACT_APP_API_URL;

  const token = localStorage.getItem("token") || "";

  const fetchAvaliacoes = async (tab: Tab) => {
    setLoading(true);
    setError(null);
    try {
      const url =
        tab === "pendentes"
          ? `${BASE_URL}/admins/avaliacoes/pendentes`
          : `${BASE_URL}/admins/avaliacoes`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Transformar IDs de fotos em URLs
      const avaliacoesComUrls = res.data.map((av: any) => ({
        ...av,
        fotos: av.fotos.map((id: number) => `/uploads/${id}`),
      }));

      setAvaliacoes(avaliacoesComUrls);
    } catch (err) {
      console.error(err);
      setError("Erro ao buscar avaliações");
    } finally {
      setLoading(false);
    }
  };

  const alterarStatus = async (id: number, status: "APROVADO" | "REJEITADO") => {
    try {
      await axios.patch(
        `${BASE_URL}/admins/avaliacoes/${id}/status`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { status },
        }
      );
      fetchAvaliacoes(activeTab);
    } catch (err) {
      console.error(err);
      setError("Erro ao atualizar status da avaliação");
    }
  };

  useEffect(() => {
    fetchAvaliacoes(activeTab);
  }, [activeTab]);

  const toggleDropdown = (id: number) => {
    setOpenDropdowns((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  if (loading) return <p>Carregando avaliações...</p>;
  if (error) return <p>{error}</p>;

  return (
    <div className={sharedStyles.container}>
      <h1>Avaliações</h1>

      {/* Tabs */}
      <div className={adminStyles.tabs}>
        <button
          className={`${adminStyles.tabButton} ${activeTab === "pendentes" ? adminStyles.activeTab : ""}`}
          onClick={() => setActiveTab("pendentes")}
        >
          Pendentes
        </button>
        <button
          className={`${adminStyles.tabButton} ${activeTab === "todas" ? adminStyles.activeTab : ""}`}
          onClick={() => setActiveTab("todas")}
        >
          Todas
        </button>
      </div>

      {/* Lista */}
      {avaliacoes.length === 0 ? (
        <p>Sem avaliações encontradas</p>
      ) : (
        <div className={adminStyles.list}>
          {avaliacoes.map((av) => {
            const isOpen = openDropdowns.includes(av.id);
            return (
              <div key={av.id} className={adminStyles.card}>
                {/* Header do dropdown */}
                <div
                  className={`${adminStyles.dropdownHeader} ${isOpen ? adminStyles.open : ""}`}
                  onClick={() => toggleDropdown(av.id)}
                >
                  <span className={adminStyles.dropdownTitle}>
                    <strong>{av.pacoteNome}</strong> -
                    <span className={adminStyles.rating}>
                      {av.nota} <span className={adminStyles.star}>★</span>
                    </span>
                  </span>
                  <span className={adminStyles.dropdownIcon}>
                    {isOpen ? "▲" : "▼"}
                  </span>
                </div>

                {/* Conteúdo do dropdown */}
                {isOpen && (
                  <div className={adminStyles.dropdownContent}>
                    {/* Informações principais */}
                    <div className={adminStyles.cardInfo}>
                      <div className={adminStyles.infoRow}>
                        <strong>Usuário:</strong> {av.usuarioNome}{" "}
                        {av.usuarioEmail && `(${av.usuarioEmail})`}
                      </div>
                      <div className={adminStyles.infoRow}>
                        <strong>Motorista:</strong> {av.motoristaNome}
                      </div>
                      <div className={adminStyles.infoRow}>
                        <strong>Comentário:</strong> {av.comentario}
                      </div>
                      <div className={adminStyles.infoRow}>
                        <strong>Status:</strong>{" "}
                        <span
                          className={`${adminStyles.statusBadge} ${av.status === "PENDENTE"
                            ? adminStyles.pending
                            : av.status === "APROVADO"
                              ? adminStyles.approved
                              : adminStyles.rejected
                            }`}
                        >
                          {av.status}
                        </span>
                      </div>
                    </div>

                    {/* Fotos gerais */}
                    {av.fotos.length > 0 && (
                      <div className={adminStyles.fotos}>
                        {av.fotos.map((url: string, idx: number) => (
                          <img key={idx} src={`${BASE_URL}${url}`} alt="Foto geral" />
                        ))}
                      </div>
                    )}

                    {/* Fotos por ponto */}
                    {av.fotosPorPonto.length > 0 && (
                      <div className={adminStyles.fotosPorPonto}>
                        {av.fotosPorPonto.map((f: any, idx: number) => (
                          <div key={idx} className={adminStyles.pontoCard}>
                            <strong>{f.ponto}:</strong>
                            <img src={`${BASE_URL}${f.url}`} alt={`Foto do ponto ${f.ponto}`} />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Botões de ação */}
                    {activeTab === "pendentes" || activeTab === "todas" ? (
                      <div className={adminStyles.actions}>
                        {/* Se não está aprovado, mostra aprovar */}
                        {(av.status === "PENDENTE" || av.status === "REJEITADO") && (
                          <button
                            className={adminStyles.aprovarButton}
                            onClick={() => alterarStatus(av.id, "APROVADO")}
                          >
                            <Check size={16} style={{ marginRight: "6px" }} />
                            <span style={{ marginLeft: "6px" }}>Aprovar</span>
                          </button>
                        )}

                        {/* Se não está rejeitado, mostra rejeitar */}
                        {(av.status === "PENDENTE" || av.status === "APROVADO") && (
                          <button
                            className={adminStyles.rejeitarButton}
                            onClick={() => alterarStatus(av.id, "REJEITADO")}
                          >
                            <X size={16} style={{ marginRight: "6px" }} />
                            <span style={{ marginLeft: "6px" }}>Rejeitar</span>
                          </button>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminAvaliacoes;